import { ROLE_TYPES } from "../enums/Roles";

/**
 * Which roles reach which resource, at page level. This is the single source of
 * truth for that question.
 *
 * App.js registers every <Resource> unconditionally and holds no role
 * conditionals. authProvider.canAccess reads this map, and react-admin does the
 * rest: ResourceMenuItem returns null when canAccess is false, so the menu entry
 * disappears, and useListController -> useRequireAccess redirects to
 * /access-denied, so the page is blocked. NavigateToFirstResource also honours it,
 * via useFirstResourceWithListAccess, so the post-login landing page is a resource
 * the role can actually open.
 *
 * Adding a page therefore means: add the <Resource> in App.js, add one line here.
 *
 * Anything absent from the map is allowed. That default matters: canAccess runs on
 * every list/show/edit/create, so a resource we forgot stays reachable instead of
 * silently disappearing behind /access-denied.
 *
 * Not covered here: custom routes nested inside a resource, such as
 * products/bulk-create. canAccess is per resource and action, not per custom
 * route, so those keep their own check in App.js.
 */

/*
 * CUSTOMER is deliberately absent from every list below: that role uses the mobile
 * app and is rejected outright by authProvider.login and checkAuth, so it never
 * reaches a resource. RESTAURANT_DRAFT is absent too — it only gets the onboarding
 * CustomRoute.
 */
const ADMIN_AND_RESTAURANT = [ROLE_TYPES.ADMIN, ROLE_TYPES.RESTAURANT];

/*
 * Back-office administration screens. Until the v5 upgrade these were gated on
 * `permissions !== RESTAURANT_DRAFT`, which let a restaurant open them by URL even
 * though its menu never linked them — they just showed an empty list. Access now
 * matches the menu.
 */
const ADMIN_ONLY = [ROLE_TYPES.ADMIN];

const RESOURCE_ROLES = {
  // Admin only.
  users: ADMIN_ONLY,
  "subscription-plans": ADMIN_ONLY,
  "restaurant-subscription-plans": ADMIN_ONLY,
  "discount-coupons": ADMIN_ONLY,
  banners: ADMIN_ONLY,
  "restaurant-mp-tokens": ADMIN_ONLY,
  sections: ADMIN_ONLY,
  videos: ADMIN_ONLY,
  "community-posts": ADMIN_ONLY,
  "product-events": ADMIN_ONLY,
  "restaurants-draft": ADMIN_ONLY,
  "products-draft": ADMIN_ONLY,

  // Restaurant only. The admin does not reach this one either — that is how it was
  // gated before the unification, and it is kept on purpose.
  "purchases/activity": [ROLE_TYPES.RESTAURANT],

  // Shared. These are the ones a restaurant has in its menu.
  purchases: ADMIN_AND_RESTAURANT,
  restaurants: ADMIN_AND_RESTAURANT,
  "restaurants/stats": ADMIN_AND_RESTAURANT,
  products: ADMIN_AND_RESTAURANT,
  coupons: ADMIN_AND_RESTAURANT,
  reviews: ADMIN_AND_RESTAURANT,
  "redeemed-rewards": ADMIN_AND_RESTAURANT,

  // Administration screens a restaurant must not reach — see ADMIN_ONLY above.
  categories: ADMIN_ONLY,
  regions: ADMIN_ONLY,
  "place-types": ADMIN_ONLY,
  "place-targets": ADMIN_ONLY,
  districts: ADMIN_ONLY,
  reports: ADMIN_ONLY,
  billing: ADMIN_ONLY,
};

/*
 * Actions that are more restricted than their resource. A restaurant can list, show
 * and edit its own restaurant, but only an admin creates one.
 *
 * react-admin hides the matching button on its own — CreateButton, EditButton,
 * ShowButton and DeleteButton all call useCanAccess — so listing an action here both
 * blocks the page and removes the button that leads to it.
 */
const RESOURCE_ACTION_ROLES = {
  restaurants: { create: ADMIN_ONLY },
};

/**
 * @param {object} params
 * @param {string} params.resource resource name, as declared in App.js
 * @param {string} [params.action] 'list' | 'show' | 'edit' | 'create' | 'delete'
 * @param {string} params.role the role from authProvider.getPermissions()
 * @returns {boolean} whether the role reaches that resource and action
 */
export const canRoleAccessResource = ({ resource, action, role }) => {
  const actionRoles = RESOURCE_ACTION_ROLES[resource]?.[action];
  if (actionRoles) return actionRoles.includes(role);

  const allowedRoles = RESOURCE_ROLES[resource];

  // Unknown resource: allow. See the note above on why the default is permissive.
  if (!allowedRoles) return true;

  return allowedRoles.includes(role);
};
